import React, { useMemo, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import Chip from '@/components/General/Chip';
import SolidButton from '@/components/General/SolidButton';
import SegmentedTabs from '@/components/General/SegmentedTabs';
import Input from '@/components/General/inputs/Input';
import SelectInput from '@/components/General/inputs/SelectInput';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import IconPickerInput from '@/components/General/inputs/IconPickerInput';
import MilestoneBadge from '@/components/Milestones/MilestoneBadge';
import MilestoneThresholdEditor from '@/components/Milestones/MilestoneThresholdEditor';
import { Tooltip } from '@/components/General/Tooltip';
import { Query } from '@/utils/react-query/Queries';
import { MILESTONE_SCOPES, MILESTONE_PRESENTATION } from '@/config/milestones';
import { allowedPlaceholdersForScope, buildMilestoneSavePayload, renderMilestoneTemplate, sortMilestoneThresholds, validateMilestoneDefinition } from '@/utils/milestones';

/** Catalog filter entity → existing SheetSelector type. */
const FILTER_ENTITY_TYPES = {
    calendars: 'calendars',
    events: 'events',
    funds: 'contributionFunds',
    contributionFunds: 'contributionFunds',
    methods: 'contributionMethods',
    contributionMethods: 'contributionMethods',
    trainingModules: 'trainingModules',
    modules: 'trainingModules',
    forms: 'forms',
    volunteerTeams: 'taskTeams',
    taskTeams: 'taskTeams',
    tasks: 'tasks',
    paths: 'paths',
    boards: 'boards',
    smallGroupTypes: 'smallGroupTypes',
    roles: 'roles',
};

const resolveFilterType = (entity) => FILTER_ENTITY_TYPES[entity] ?? entity;

/** Object used when a saved filter ID no longer resolves to a live record. */
const missingSelection = (id) => ({ id, name: 'Unavailable selection', title: 'Unavailable selection' });

const SCOPE_TABS = [
    { key: MILESTONE_SCOPES.ORGANIZATION, label: 'Organization' },
    { key: MILESTONE_SCOPES.INDIVIDUAL, label: 'Individual' },
];

/** Human-readable meaning of each template placeholder for the insert chips. */
const PLACEHOLDER_DESCRIPTIONS = {
    threshold: 'The tier value that was reached (e.g. 50)',
    metricValue: 'The current total count that triggered the celebration',
    milestoneName: "This milestone series' name",
    organizationName: "Your organization's name",
    personFirstName: 'The first name of the person who earned the tier',
};

/**
 * Preset chips under a copy input — tap inserts the variable (appended to the
 * current value), hover/long-press shows what it resolves to.
 */
const PlaceholderChipRow = ({ placeholders, field, onInsert }) => (
    <View style={styles.chipRow}>
        {placeholders.map(p => (
            <Tooltip
                key={p}
                title={PLACEHOLDER_DESCRIPTIONS[p] ?? 'Dynamic value'}
                onPress={() => onInsert(p)}
                testID={`milestone-var-${field}-${p}`}
            >
                <Chip colorVariant="textSecondary" icon="code-outline">{`{{${p}}}`}</Chip>
            </Tooltip>
        ))}
    </View>
);

/** Append a {{name}} token, keeping a space between it and existing copy. */
const appendPlaceholder = (value, name) => {
    const token = `{{${name}}}`;
    if (!value) return token;
    return (value.endsWith(' ') || value.endsWith('\n') ? value : `${value} `) + token;
};

/**
 * Reusable definition editor for organization/individual milestone series.
 * Presentational — the parent route owns fetch/save mutations and revision.
 */
const MilestoneEditor = ({ catalog, definition = null, initialScope = MILESTONE_SCOPES.ORGANIZATION, loggedInUser, saving = false, readOnly = false, onSubmit, onCancel }) => {
    const { theme: { colors } } = useUnistyles();
    const isEditing = !!definition;
    const archived = !!definition?.deletedAt;
    const triggerLocked = isEditing && definition?.hasAwards === true;
    const earnedThresholdIDs = definition?.earnedThresholdIDs ?? [];

    const [scope, setScope] = useState(definition?.scope ?? initialScope);
    const [metricKey, setMetricKey] = useState(definition?.metricKey ?? null);
    const [filterSelections, setFilterSelections] = useState(() => {
        // ID lists → placeholder objects; resolved to live records below.
        const initial = {};
        for (const [key, ids] of Object.entries(definition?.filters ?? {})) {
            if (Array.isArray(ids)) initial[key] = ids.map(id => ({ id }));
        }
        return initial;
    });
    const [thresholds, setThresholds] = useState(() => sortMilestoneThresholds(definition?.thresholds ?? []).map(t => ({ id: t.id ?? null, threshold: t.threshold })));
    const [name, setName] = useState(definition?.name ?? '');
    const [title, setTitle] = useState(definition?.title ?? MILESTONE_PRESENTATION.defaultTitle);
    const [message, setMessage] = useState(definition?.message ?? '');
    const [badgeIcon, setBadgeIcon] = useState(definition?.badgeIcon ?? MILESTONE_PRESENTATION.defaultBadgeIcon);
    const [badgeColorToken, setBadgeColorToken] = useState(definition?.badgeColorToken ?? MILESTONE_PRESENTATION.defaultBadgeColorToken);
    const [includeAdministrators, setIncludeAdministrators] = useState(definition?.includeAdministrators ?? (initialScope === MILESTONE_SCOPES.ORGANIZATION));
    const [roleIDs, setRoleIDs] = useState(definition?.roleIDs ?? []);
    const [enabled, setEnabled] = useState(definition?.enabled ?? false);
    const [errors, setErrors] = useState({});

    const metric = useMemo(() => catalog?.metrics?.find(m => m.key === metricKey) ?? null, [catalog, metricKey]);
    const metricOptions = useMemo(() =>
        (catalog?.metrics ?? [])
            .filter(m => (m.scopes ?? []).includes(scope))
            .map(m => ({ label: m.label, value: m.key, subtitle: m.usesTrackingStart ? 'Counts activity after tracking begins' : undefined })),
    [catalog, scope]);

    /* ---- Entity lists for filter pickers + saved-ID label resolution ---- */
    const neededTypes = useMemo(() => new Set((metric?.filters ?? []).map(f => resolveFilterType(f.entity))), [metric]);
    const needs = (type) => neededTypes.has(type);
    const { data: calendars } = Query.calendars.all({ options: { enabled: needs('calendars') } });
    const { data: events } = Query.events.all({ options: { enabled: needs('events') } });
    const { data: funds } = Query.contributionFunds.all({ options: { enabled: needs('contributionFunds') } });
    const { data: methods } = Query.contributionMethods.all({ options: { enabled: needs('contributionMethods') } });
    const { data: teams } = Query.taskTeams.all({ options: { enabled: needs('taskTeams') } });
    const { data: tasks } = Query.tasks.all({ options: { enabled: needs('tasks') } });
    const { data: paths } = Query.paths.all({ options: { enabled: needs('paths') } });
    const { data: smallGroupTypes } = Query.smallGroupTypes.all({ options: { enabled: needs('smallGroupTypes') } });
    const { data: roles } = Query.roles.all({ user: loggedInUser, options: { enabled: scope === MILESTONE_SCOPES.ORGANIZATION } });
    const { data: forms } = Query.forms.all({ options: { enabled: needs('forms') } });
    const { data: boards } = Query.boards.all({ options: { enabled: needs('boards') } });
    const modulesQuery = Query.trainingModules.all({ options: { enabled: needs('trainingModules') } });
    const trainingModules = useMemo(
        () => (modulesQuery.data?.pages ?? []).flatMap(page => page?.data ?? []),
        [modulesQuery.data]
    );

    const entityData = useMemo(() => ({
        calendars: calendars ?? [],
        events: events ?? [],
        contributionFunds: funds ?? [],
        contributionMethods: methods ?? [],
        taskTeams: teams ?? [],
        tasks: tasks ?? [],
        paths: paths ?? [],
        smallGroupTypes: smallGroupTypes ?? [],
        roles: roles ?? [],
        forms: forms ?? [],
        boards: boards ?? [],
        trainingModules,
    }), [calendars, events, funds, methods, teams, tasks, paths, smallGroupTypes, roles, forms, boards, trainingModules]);

    /** Saved filter IDs → picker objects (live record or unavailable marker). */
    const selectionsFor = (filterKey, type) =>
        (filterSelections[filterKey] ?? []).map(sel => {
            const live = (entityData[type] ?? []).find(item => item.id === sel.id);
            return live ?? (sel.name ? sel : missingSelection(sel.id));
        });

    const roleSelections = useMemo(
        () => roleIDs.map(id => (roles ?? []).find(r => r.id === id) ?? missingSelection(id)),
        [roleIDs, roles]
    );

    const handleMetricChange = (key) => {
        if (triggerLocked || archived) return;
        setMetricKey(key);
        setFilterSelections({});
    };

    const handleScopeChange = (nextScope) => {
        if (isEditing || archived) return;
        setScope(nextScope);
        const nextMetric = catalog?.metrics?.find(m => m.key === metricKey);
        if (nextMetric && !(nextMetric.scopes ?? []).includes(nextScope)) setMetricKey(null);
        setFilterSelections({});
        if (nextScope === MILESTONE_SCOPES.INDIVIDUAL) {
            setIncludeAdministrators(false);
            setRoleIDs([]);
        }
    };

    const editorState = {
        id: definition?.id ?? null,
        scope,
        metricKey,
        filters: Object.fromEntries(Object.entries(filterSelections).map(([key, sels]) => [key, (sels ?? []).map(s => s.id).filter(Boolean)])),
        thresholds,
        name,
        title,
        message,
        badgeIcon,
        badgeColorToken,
        includeAdministrators,
        roleIDs,
        enabled,
    };

    const allowed = allowedPlaceholdersForScope(scope, catalog);
    const previewValues = {
        threshold: sortMilestoneThresholds(thresholds)[0]?.threshold ?? 10,
        metricValue: sortMilestoneThresholds(thresholds)[0]?.threshold ?? 10,
        milestoneName: name || 'Milestone name',
        organizationName: 'Your Church',
        personFirstName: loggedInUser?.firstName ?? 'Alex',
    };

    const submit = () => {
        const { valid, errors: validationErrors } = validateMilestoneDefinition(editorState, catalog);
        setErrors(validationErrors);
        if (!valid) return;
        onSubmit?.(buildMilestoneSavePayload(editorState, definition));
    };

    // The parent route gates page access; archived series render read-only.
    const formDisabled = archived || readOnly;

    return (
        <KeyboardAwareScrollView style={styles.scroll} contentContainerStyle={styles.content} bottomOffset={60} keyboardShouldPersistTaps="handled">
            {archived && (
                <View style={styles.notice}>
                    <Unicon name="archive-outline" size={18} colorVariant="warning" />
                    <Text style={styles.noticeText}>This milestone is archived. Earned badges and history remain, but the series can no longer be edited or awarded.</Text>
                </View>
            )}
            {triggerLocked && !archived && (
                <View style={styles.notice}>
                    <Unicon name="lock-closed-outline" size={18} colorVariant="info" />
                    <Text style={styles.noticeText}>Badges have already been earned on this series — the trigger, filters, and badge design are permanent. Duplicate it to create a changed copy.</Text>
                </View>
            )}

            {/* Enablement — top of the form so the series' live state is
                visible before the detail fields. */}
            {!archived && (
                <ToggleRow
                    testID="milestone-enabled"
                    label="Enabled"
                    description={enabled ? 'Enabling counts all matching history — existing activity may award tiers immediately and trigger one catch-up celebration.' : undefined}
                    value={enabled}
                    onValueChange={setEnabled}
                    disabled={formDisabled}
                />
            )}

            {/* Scope — chosen first on create, fixed afterward. */}
            <View style={styles.field}>
                <Text style={styles.fieldLabel}>Who does this milestone celebrate?</Text>
                <SegmentedTabs
                    tabs={SCOPE_TABS}
                    value={scope}
                    onChange={handleScopeChange}
                    disabled={isEditing || archived}
                    testIDPrefix="milestone-scope"
                />
                {!!errors.scope && <Text style={styles.errorText}>{errors.scope}</Text>}
            </View>

            {/* Trigger metric */}
            <View testID="milestone-trigger">
                <SelectInput
                    title="Trigger"
                    helpText="The activity that counts toward this milestone."
                    placeholder="Choose what counts"
                    required
                    disabled={formDisabled || triggerLocked}
                    options={metricOptions}
                    value={metricKey}
                    onUpdate={handleMetricChange}
                />
            </View>
            {!!errors.metricKey && <Text style={styles.errorText}>{errors.metricKey}</Text>}
            {metric?.usesTrackingStart === true && (
                <View style={styles.notice}>
                    <Unicon name="time-outline" size={18} colorVariant="info" />
                    <Text style={styles.noticeText}>
                        Days Active in Reach counts days from when milestone tracking began{catalog?.trackingStartedAt ? ` (${new Date(catalog.trackingStartedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})` : ''} — earlier activity can&apos;t be counted.
                    </Text>
                </View>
            )}
            {metric?.privacyClass === 'FINANCIAL' && (
                <View style={styles.notice}>
                    <Unicon name="lock-closed-outline" size={18} colorVariant="warning" />
                    <Text style={styles.noticeText}>Badges from donation metrics are only visible to the earner and viewers with View Contributions.</Text>
                </View>
            )}

            {/* Scope filters */}
            {(metric?.filters ?? []).map((filter) => {
                const type = resolveFilterType(filter.entity);
                const selections = selectionsFor(filter.key, type);
                const missing = selections.filter(sel => sel.name === 'Unavailable selection');
                const isMultiple = filter.multiple !== false;
                // SheetSelector dereferences Query[type].all — catalog entities
                // without a registered selector type get a safe notice instead.
                const selectorSupported = typeof Query[type]?.all === 'function';
                return (
                    <View key={filter.key}>
                        {selectorSupported ? (
                            <SheetSelector
                                title={filter.label}
                                type={type}
                                multiple={isMultiple}
                                required={filter.required === true}
                                disabled={formDisabled || triggerLocked}
                                value={isMultiple ? selections : (selections[0] ?? null)}
                                onChange={(items) => setFilterSelections(prev => ({ ...prev, [filter.key]: isMultiple ? (items ?? []) : (items ? [items] : []) }))}
                                queryResult={type === 'trainingModules' ? { data: trainingModules, isFetching: modulesQuery.isFetching } : null}
                                placeholder={filter.required ? `Choose a ${filter.label}` : `All ${filter.label} (optional)`}
                                helpText={isMultiple ? 'Any selected item counts. Leave empty to count everything applicable.' : 'Choose one.'}
                            />
                        ) : (
                            <Text style={styles.warningText}>The {filter.label} filter can&apos;t be edited in this version of the app yet.</Text>
                        )}
                        {missing.length > 0 && (
                            <Text style={styles.warningText}>{missing.length} saved selection{missing.length > 1 ? 's are' : ' is'} no longer available — kept so the scope doesn&apos;t silently broaden.</Text>
                        )}
                        {!!errors[`filters.${filter.key}`] && <Text style={styles.errorText}>{errors[`filters.${filter.key}`]}</Text>}
                    </View>
                );
            })}

            {/* Thresholds */}
            <MilestoneThresholdEditor
                thresholds={thresholds}
                earnedThresholdIDs={earnedThresholdIDs}
                disabled={formDisabled}
                maxThresholds={catalog?.thresholdBounds?.max ?? null}
                onChange={setThresholds}
                error={errors.thresholds}
            />

            {/* Copy */}
            <Input title="Milestone Name" required placeholder="e.g. Faithful Volunteer" value={name} onUpdate={setName} disabled={formDisabled} maxLength={catalog?.textBounds?.name?.max} />
            {!!errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
            <Input
                title="Celebration Title"
                placeholder={MILESTONE_PRESENTATION.defaultTitle}
                helpText={`Placeholders: ${allowed.map(p => `{{${p}}}`).join(', ')}`}
                value={title}
                onUpdate={setTitle}
                disabled={formDisabled}
                maxLength={catalog?.textBounds?.title?.max}
            />
            {!!errors.title && <Text style={styles.errorText}>{errors.title}</Text>}
            {!formDisabled && (
                <PlaceholderChipRow placeholders={allowed} field="title" onInsert={(p) => setTitle(prev => appendPlaceholder(prev, p))} />
            )}
            <Input
                title="Celebration Message"
                multiline
                placeholder="e.g. Your organization reached {{threshold}} people in one week!"
                value={message}
                onUpdate={setMessage}
                disabled={formDisabled}
                maxLength={catalog?.textBounds?.message?.max}
            />
            {!!errors.message && <Text style={styles.errorText}>{errors.message}</Text>}
            {!formDisabled && (
                <PlaceholderChipRow placeholders={allowed} field="message" onInsert={(p) => setMessage(prev => appendPlaceholder(prev, p))} />
            )}

            {/* Badge design */}
            <View style={styles.field}>
                <IconPickerInput
                    title="Badge Icon"
                    options={catalog?.badgeIcons ?? [badgeIcon]}
                    value={badgeIcon}
                    disabled={formDisabled || triggerLocked}
                    onUpdate={(icon) => setBadgeIcon(icon ?? MILESTONE_PRESENTATION.defaultBadgeIcon)}
                />
                {!!errors.badgeIcon && <Text style={styles.errorText}>{errors.badgeIcon}</Text>}
            </View>
            <View style={styles.field}>
                <Text style={styles.fieldLabel}>Badge Color</Text>
                <View style={styles.swatchGrid}>
                    {(catalog?.badgeColorTokens ?? [badgeColorToken]).map(token => {
                        const selected = token === badgeColorToken;
                        const swatchDisabled = formDisabled || triggerLocked;
                        return (
                            <Pressable
                                key={token}
                                testID={`milestone-color-${token}`}
                                disabled={swatchDisabled}
                                onPress={() => setBadgeColorToken(token)}
                                accessibilityLabel={`Badge color ${token}`}
                                style={styles.swatch(selected, colors[token] ?? colors.primary, swatchDisabled)}
                            >
                                {selected && <Unicon name="checkmark" size={16} colorVariant="onPrimary" />}
                            </Pressable>
                        );
                    })}
                </View>
                {!!errors.badgeColorToken && <Text style={styles.errorText}>{errors.badgeColorToken}</Text>}
            </View>

            {/* Audience — organization scope only */}
            {scope === MILESTONE_SCOPES.ORGANIZATION && (
                <View style={styles.field}>
                    <Text style={styles.fieldLabel}>Who celebrates?</Text>
                    <Text style={styles.fieldHelp}>Organization milestones appear to the selected audience on their Dashboard.</Text>
                    <ToggleRow
                        testID="milestone-include-admins"
                        label="Include Administrators"
                        value={includeAdministrators}
                        onValueChange={setIncludeAdministrators}
                        disabled={formDisabled}
                    />
                    <SheetSelector
                        title="Roles"
                        type="roles"
                        multiple
                        disabled={formDisabled}
                        value={roleSelections}
                        onChange={(items) => setRoleIDs((items ?? []).map(r => r.id).filter(Boolean))}
                        placeholder="Select roles (optional)"
                        helpText="Members of these roles also see the celebration. No audience selected means nobody sees it."
                    />
                    {!!errors.audience && <Text style={styles.errorText}>{errors.audience}</Text>}
                </View>
            )}

            {/* Inline preview */}
            <View style={styles.previewCard} testID="milestone-preview">
                <Text style={styles.previewHeading}>Preview</Text>
                <View style={styles.previewBody}>
                    <MilestoneBadge icon={badgeIcon} colorToken={badgeColorToken} threshold={previewValues.threshold} seriesName={name} size="lg" />
                    <View style={styles.previewCopy}>
                        <Text style={styles.previewTitle}>{renderMilestoneTemplate(title || MILESTONE_PRESENTATION.defaultTitle, previewValues, allowed)}</Text>
                        {!!message && <Text style={styles.previewMessage}>{renderMilestoneTemplate(message, previewValues, allowed)}</Text>}
                    </View>
                </View>
            </View>

            {/* Actions */}
            {!archived && (
                <View style={styles.actions}>
                    {!!onCancel && (
                        <SolidButton testID="milestone-cancel" variant="outlined" type="textSecondary" text="Cancel" onPress={onCancel} />
                    )}
                    <SolidButton
                        testID="milestone-save"
                        text={isEditing ? 'Save Changes' : 'Create Milestone'}
                        iconName="checkmark"
                        processing={saving}
                        disabled={formDisabled}
                        onPress={submit}
                    />
                </View>
            )}
        </KeyboardAwareScrollView>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, shadows }) => ({
    scroll: {
        flex: 1,
    },
    content: {
        padding: spacing.md,
        gap: spacing.sm,
        _web: {
            maxWidth: 720,
            alignSelf: 'center',
            width: '100%',
        },
    },
    field: {
        marginVertical: spacing.xs,
        gap: spacing.xs,
    },
    fieldLabel: {
        color: colors.textSecondary,
        fontFamily: fonts.heading,
        fontSize: 13,
    },
    fieldHelp: {
        color: colors.textTertiary,
        fontFamily: fonts.body,
        fontSize: 12,
        lineHeight: 16,
    },
    notice: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: spacing.sm,
        backgroundColor: colors.infoContainer,
        borderRadius: roundness.md,
        padding: spacing.md,
    },
    noticeText: {
        flex: 1,
        color: colors.text,
        fontFamily: fonts.body,
        fontSize: 13,
        lineHeight: 18,
    },
    chipRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: spacing.xs,
        marginTop: -spacing.xs,
        marginBottom: spacing.xs,
    },
    swatchGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: spacing.sm,
    },
    swatch: (selected, color, disabled) => ({
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: color,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: selected ? 2 : 0,
        borderColor: selected ? colors.text : 'transparent',
        // Locked series (awards earned / read-only) previously rendered
        // swatches identical but non-interactive — dim them so the dead
        // press doesn't look like a bug.
        opacity: disabled ? 0.45 : 1,
        _web: { cursor: disabled ? 'not-allowed' : 'pointer' },
    }),
    previewCard: {
        marginTop: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: roundness.lg,
        padding: spacing.md,
        boxShadow: shadows.card,
        gap: spacing.sm,
    },
    previewHeading: {
        color: colors.textSecondary,
        fontFamily: fonts.bold,
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    previewBody: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.md,
    },
    previewCopy: {
        flex: 1,
        gap: spacing.xs,
    },
    previewTitle: {
        color: colors.text,
        fontFamily: fonts.heading,
        fontSize: 16,
    },
    previewMessage: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 13,
        lineHeight: 18,
    },
    actions: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: spacing.sm,
        marginTop: spacing.md,
        marginBottom: spacing.xxl,
    },
    errorText: {
        color: colors.error,
        fontFamily: fonts.body,
        fontSize: 12,
        marginTop: -spacing.xs,
    },
    warningText: {
        color: colors.warning,
        fontFamily: fonts.body,
        fontSize: 12,
    },
}));

export default MilestoneEditor;
